import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import AdminLayout from "../../../components/AdminLayout";
import ContentBox from "../../../components/ContentBox";
import { getBannerOffsets, upsertBannerOffset, deleteBannerOffset } from "../../../services/api";

// ─── Exact constants from Banner.jsx ─────────────────────────────────────────
const ALIGN_OFFSET  = 32;
const TILE_W        = 32;
const TILE_H        = 16;
const BASE_BAND_H   = 10;
const GRID_ANCHOR_Y = TILE_H / 2;

const FURNI_SLOTS = [
    { dx: 448,  dy: 25,                  z: 20 },
    { dx: 672,  dyNonRug: 74, dyRug: 70, z: 25 },
    { dx: 832,  dy: 58,                  z: 30 },
    { dx: 992,  dy: 74,                  z: 35 },
    { dx: 1152, dyNonRug: 25, dyRug: 23, z: 40 },
];

// The inner banner world is rendered at a fixed width so coordinates are stable.
// We use 1400px — a typical desktop viewport. The max-w-[1200px] mx-auto px-4
// container centres at (1400-1200)/2 = 100px, so logoX = 100 + 16 = 116.
const INNER_W   = 1400;
const LOGO_X    = 116; // (1400 - 1200) / 2 + 16 (px-4)
const BG_X      = LOGO_X - ALIGN_OFFSET; // 84

// The preview window clips the inner world
const PREVIEW_W = 160;
const PREVIEW_H = 175;

const FURNI_API_URL = process.env.REACT_APP_FURNI_API_URL || "https://api.rhyss.dev/api/furni/latest";

// ─── Helpers ─────────────────────────────────────────────────────────────────
function parseSize(size) {
    const parts = (size || "1x1").split("x");
    return [parseInt(parts[0], 10) || 1, parseInt(parts[1], 10) || 1];
}

// largeImages is [stateIndex][rotationIndex] = url string
function getImageOptions(largeImages) {
    const opts = [];
    if (!Array.isArray(largeImages)) return opts;
    const multiState = largeImages.length > 1;
    largeImages.forEach((group, si) => {
        if (!Array.isArray(group)) return;
        group.forEach((url, ri) => {
            if (!url || typeof url !== "string") return;
            opts.push({
                label:         multiState ? `State ${si + 1}, Rotation ${ri + 1}` : `Rotation ${ri + 1}`,
                stateIndex:    si,
                rotationIndex: ri,
                url,
            });
        });
    });
    return opts;
}

function normaliseFurniWithImages(results) {
    if (!Array.isArray(results)) return [];
    const items = [];
    for (let i = 0; i < results.length; i++) {
        const raw    = results[i];
        const [w, h] = parseSize(raw.size);
        const isSquare  = w === h && w <= 3;
        const isOddSize = !isSquare && (w === 1 || h === 1);
        if (!isSquare && !isOddSize) continue;
        const imageOpts = getImageOptions(raw.largeImages);
        if (!imageOpts.length) continue;
        items.push({
            id:        raw._id || raw.name || String(i),
            name:      raw.name || "",
            url:       imageOpts[0].url,
            imageOpts,
            size:      raw.size,
            type:      raw.type,
            stand:     raw.stand,
        });
    }
    return items;
}

// ─── Exact predicates + slot-fill from Banner.jsx ────────────────────────────
const is1x1      = (it) => it && it.type === "Floor" && it.size === "1x1";
const isBigStand = (it) => it && it.type === "Floor" && (it.size === "2x2" || it.size === "3x3") && it.stand === true;

function getBannerSlots(furniItems) {
    const items = Array.isArray(furniItems) ? furniItems.filter(Boolean) : [];
    const used  = new Array(items.length).fill(false);
    const takeRange = (s, e, pred) => {
        for (let i = Math.max(0, s); i < Math.min(items.length, e); i++) {
            if (used[i] || !pred(items[i])) continue;
            used[i] = true; return items[i];
        }
        return null;
    };
    const takeAny = (pred) => takeRange(0, 5, pred) || takeRange(5, items.length, pred);
    const out = [null, null, null, null, null];
    out[0] = takeAny(is1x1);
    out[2] = takeAny(is1x1);
    out[3] = takeAny(is1x1);
    out[1] = takeRange(0, 5, isBigStand) || takeAny(is1x1) || takeRange(5, items.length, isBigStand);
    out[4] = takeRange(0, 5, isBigStand) || takeAny(is1x1) || takeRange(5, items.length, isBigStand);
    return out;
}

function resolveSlotDY(slot, slotIndex, isRug) {
    if (slotIndex !== 1 && slotIndex !== 4) return slot.dy ?? 0;
    return isRug ? (slot.dyRug ?? slot.dy ?? 0) : (slot.dyNonRug ?? slot.dy ?? 0);
}

// ─── computeShiftFromImage — verbatim from Banner.jsx ────────────────────────
function useComputeShift() {
    const [spriteShift,  setSpriteShift]  = useState({});
    const [spriteBBoxH,  setSpriteBBoxH]  = useState({});

    const compute = useCallback((imgEl, key, item) => {
        if (!imgEl) return;
        const w = imgEl.naturalWidth || 0, h = imgEl.naturalHeight || 0;
        if (!w || !h) return;
        try {
            const canvas = document.createElement("canvas");
            canvas.width = w; canvas.height = h;
            const ctx = canvas.getContext("2d", { willReadFrequently: true });
            if (!ctx) return;
            ctx.clearRect(0, 0, w, h);
            ctx.drawImage(imgEl, 0, 0);
            const data = ctx.getImageData(0, 0, w, h).data;

            let minX = w, minY = h, maxX = -1, maxY = -1;
            for (let y = 0; y < h; y++) {
                const row = y * w * 4;
                for (let x = 0; x < w; x++) {
                    if (data[row + x * 4 + 3] !== 0) {
                        if (x < minX) minX = x; if (y < minY) minY = y;
                        if (x > maxX) maxX = x; if (y > maxY) maxY = y;
                    }
                }
            }
            if (maxX < 0) { setSpriteShift(p => ({ ...p, [key]: { x: 0, y: 0 } })); return; }

            const bboxH      = maxY - minY + 1;
            setSpriteBBoxH(p => p[key] === bboxH ? p : { ...p, [key]: bboxH });

            const isBigFloor = item?.type === "Floor" && (item.size === "2x2" || item.size === "3x3");
            const treatAsRug = !!(isBigFloor && (item.stand === undefined || item.stand === true));
            const rugN       = item?.size === "3x3" ? 3 : item?.size === "2x2" ? 2 : 1;
            const rugCenterDY = treatAsRug ? (rugN === 2 ? GRID_ANCHOR_Y : rugN === 3 ? GRID_ANCHOR_Y - 2 : 0) : 0;
            const bandBottom = maxY;
            const bandTop    = Math.max(minY, maxY - Math.max(TILE_H + 4, BASE_BAND_H));
            const a = TILE_W / 2, b = TILE_H / 2;
            const step = w > 140 ? 2 : 1;
            const pts = []; let cMinX = w, cMaxX = -1;

            for (let x = minX; x <= maxX; x += step) {
                let yf = -1;
                for (let y = bandBottom; y >= bandTop; y--) { if (data[(y*w+x)*4+3] !== 0) { yf = y; break; } }
                if (yf < 0) continue;
                pts.push({ x, y: yf }); if (x < cMinX) cMinX = x; if (x > cMaxX) cMaxX = x;
            }
            if (pts.length < 6) {
                for (let x = minX; x <= maxX; x += step) {
                    let yf = -1;
                    for (let y = bandBottom; y >= minY; y--) { if (data[(y*w+x)*4+3] !== 0) { yf = y; break; } }
                    if (yf < 0) continue;
                    pts.push({ x, y: yf }); if (x < cMinX) cMinX = x; if (x > cMaxX) cMaxX = x;
                }
            }

            const filterBasePts = (arr) => {
                if (!arr || arr.length < 4) return arr;
                const sAll  = arr.slice().sort((p, q) => p.x - q.x);
                const ysAll = sAll.map(p => p.y).sort((a, b) => a - b);
                const yMax2 = ysAll[ysAll.length - 1];
                const yTol  = Math.max(6, Math.round(TILE_H * 0.4));
                const low   = (yMax2 - ysAll[0]) >= yTol*2 &&
                    (yMax2 - ysAll[Math.floor(ysAll.length*0.2)]) >= yTol*2 &&
                    (ysAll[Math.floor(ysAll.length*0.8)] - ysAll[0]) >= yTol;
                let s = sAll;
                if (low) {
                    const t = sAll.filter(p => p.y >= yMax2 - yTol);
                    if (t.length >= 4) {
                        const sa = sAll[sAll.length-1].x - sAll[0].x, st = t[t.length-1].x - t[0].x;
                        if (st >= Math.max(10, sa*0.55)) s = t;
                    }
                }
                const jy = Math.max(7, Math.round(TILE_H*0.5)), jx = Math.max(2, step*2);
                const segs = []; let cur = [s[0]];
                for (let i = 1; i < s.length; i++) {
                    const p = s[i], pv = s[i-1];
                    if (p.x - pv.x > jx || Math.abs(p.y - pv.y) > jy) { segs.push(cur); cur = [p]; } else cur.push(p);
                }
                segs.push(cur);
                if (segs.length <= 1) return s;
                const sc = seg => {
                    const n = seg.length, xs = seg[n-1].x - seg[0].x;
                    const ys = seg.map(p => p.y).sort((a,b) => a-b), ym = ys[Math.floor(ys.length/2)];
                    return { n, xs, ym, yb: (ym - bandTop) / Math.max(1, bandBottom - bandTop) };
                };
                let best = segs[0], bs = sc(best);
                for (let i = 1; i < segs.length; i++) {
                    const seg = segs[i], s2 = sc(seg);
                    const v1 = bs.xs*2 + bs.n + bs.yb*10, v2 = s2.xs*2 + s2.n + s2.yb*10;
                    if (v2 > v1 + 0.0001 || (Math.abs(v2-v1) <= 0.0001 && s2.ym > bs.ym)) { best = seg; bs = s2; }
                }
                return best;
            };

            const fp = filterBasePts(pts);
            if (fp?.length) { cMinX = w; cMaxX = -1; for (const p of fp) { if (p.x < cMinX) cMinX = p.x; if (p.x > cMaxX) cMaxX = p.x; } }
            if (!fp || fp.length < 3) { setSpriteShift(p => ({ ...p, [key]: { x: 0, y: 0 } })); return; }

            const supportW = Math.max(1, cMaxX - cMinX + 1);
            let pd = fp;
            if (supportW >= TILE_W*1.35 && bboxH <= TILE_H*1.6) {
                const ol = [];
                for (let x = minX; x <= maxX; x += step) {
                    let yb = -1; for (let y = maxY; y >= minY; y--) { if (data[(y*w+x)*4+3] !== 0) { yb = y; break; } }
                    if (yb < 0) continue;
                    let yt = -1; for (let y = minY; y <= maxY; y++) { if (data[(y*w+x)*4+3] !== 0) { yt = y; break; } }
                    ol.push({ x, y: yb }); if (yt >= 0 && yt !== yb) ol.push({ x, y: yt });
                }
                if (ol.length >= 6) pd = ol;
            }

            let uMin = Infinity, uMax = -Infinity, vMin = Infinity, vMax = -Infinity;
            for (const p of pd) {
                const u = p.x/a + p.y/b, v = p.y/b - p.x/a;
                if (u < uMin) uMin = u; if (u > uMax) uMax = u;
                if (v < vMin) vMin = v; if (v > vMax) vMax = v;
            }
            const xC = (((uMin+uMax)/2 - (vMin+vMax)/2) * a) / 2;
            const yC = (((uMin+uMax)/2 + (vMin+vMax)/2) * b) / 2;
            const bf = Math.min(1, Math.max(0, supportW / TILE_W));
            let lift = Math.round((TILE_H*0.75)*(1-bf)) + (bboxH < TILE_H ? Math.round((TILE_H-bboxH)*0.5) : 0);
            lift = Math.min(Math.max(lift, 0), TILE_H+2);
            const anchorY = Math.min(Math.max(yC - lift, Math.max(minY, maxY - TILE_H - 2)), maxY);
            const shiftX = w/2 - xC;
            const shiftY = (h-1) - anchorY - GRID_ANCHOR_Y - rugCenterDY;

            setSpriteShift(prev => {
                const pv = prev[key];
                if (pv && pv.x === shiftX && pv.y === shiftY) return prev;
                return { ...prev, [key]: { x: shiftX, y: shiftY } };
            });
        } catch {
            setSpriteShift(p => ({ ...p, [key]: { x: 0, y: 0 } }));
        }
    }, []);

    return { spriteShift, spriteBBoxH, compute };
}

// ─── BannerPreview ────────────────────────────────────────────────────────────
// Renders the full banner scene (all slots) in a wide inner div, then uses
// CSS transform to pan so the selected slot is centred in the clipping window.
// No coordinate approximations — it IS the banner, just cropped.
function BannerPreview({ slots, selectedSlotIndex, overridesBySlot, onShiftReady }) {
    const { spriteShift, spriteBBoxH, compute } = useComputeShift();

    const selectedSlot = FURNI_SLOTS[selectedSlotIndex] ?? FURNI_SLOTS[0];
    // The furni anchor in the inner world is at: left = LOGO_X + slot.dx
    // We want that centred in PREVIEW_W, so pan = (LOGO_X + slot.dx) - PREVIEW_W/2
    const panX = (LOGO_X + selectedSlot.dx) - PREVIEW_W / 2;

    // Report computed shift back to parent so the debug overlay can show it
    useEffect(() => {
        if (!onShiftReady) return;
        const item = slots[selectedSlotIndex];
        if (!item) return;
        const key = `${selectedSlotIndex}:${item.url}`;
        const sh  = spriteShift[key];
        if (sh) onShiftReady(sh);
    }, [spriteShift, selectedSlotIndex, slots, onShiftReady]);

    return (
        <div
            className="relative flex-shrink-0 overflow-hidden"
            style={{ width: PREVIEW_W, height: PREVIEW_H, border: "2px solid rgba(0,0,0,0.3)", borderRadius: 4 }}
        >
            {/* Inner banner world — full width, panned by CSS transform */}
            <div
                className="absolute top-0 left-0"
                style={{
                    width:               INNER_W,
                    height:              PREVIEW_H,
                    backgroundImage:     `url(${process.env.PUBLIC_URL}/img/red_tile.webp)`,
                    backgroundRepeat:    "repeat",
                    backgroundPosition:  `${BG_X}px 28px`,
                    imageRendering:      "pixelated",
                    transform:           `translateX(${-panX}px)`,
                    willChange:          "transform",
                }}
            >
                {slots.map((item, index) => {
                    if (!item) return null;
                    const slot    = FURNI_SLOTS[index];
                    const isBig   = item.type === "Floor" && (item.size === "2x2" || item.size === "3x3");
                    const isRug   = isBig && (item.stand === undefined || item.stand === true);
                    const bottom  = resolveSlotDY(slot, index, isRug);
                    const left    = LOGO_X + slot.dx;
                    const zIndex  = isRug ? 29 : 31;

                    // Apply overrides for this slot
                    const ov  = overridesBySlot?.[index] ?? {};
                    const url = ov.url ?? item.url;
                    const ox  = ov.offsetX ?? 0;
                    const oy  = ov.offsetY ?? 0;

                    const key = `${index}:${url}`;
                    const sh  = spriteShift[key] ?? { x: 0, y: 0 };
                    const transform = `translateX(-50%) translateX(${sh.x + ox}px) translateY(${sh.y + oy}px)`;

                    return (
                        <div key={`slot-${index}`} className="absolute pointer-events-none"
                             style={{ left, bottom, transform, zIndex }}>
                            <div className="w-[180px] h-[140px] flex items-end justify-center overflow-visible">
                                <div className="relative inline-block shrink-0">
                                    <img src={url} alt="" className="absolute left-0 top-0 block"
                                         style={{ imageRendering: "pixelated", filter: "brightness(0) saturate(100%)", opacity: 0.2, transform: "translateY(2px)", width: "auto", height: "auto", maxWidth: "none", maxHeight: "none", zIndex: 0 }}
                                         loading="eager" draggable={false} crossOrigin="anonymous" />
                                    <img src={url} alt={item.name} className="block"
                                         style={{ imageRendering: "pixelated", width: "auto", height: "auto", maxWidth: "none", maxHeight: "none", position: "relative", zIndex: 1 }}
                                         loading="eager" draggable={false} crossOrigin="anonymous"
                                         onLoad={(e) => compute(e.currentTarget, key, { ...item, url })} />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ─── Main Component ──────────────────────────────────────────────────────────
function BannerOffsets({ userData }) {
    const [allFurni,      setAllFurni]      = useState([]);
    const [loading,       setLoading]       = useState(true);
    const [selectedId,    setSelectedId]    = useState(null);
    const [imageOptIdx,   setImageOptIdx]   = useState(0);
    const [offsetX,       setOffsetX]       = useState(0);
    const [offsetY,       setOffsetY]       = useState(0);
    const [savedOffsets,  setSavedOffsets]  = useState([]);
    const [saving,        setSaving]        = useState(false);
    const [autoShift,     setAutoShift]     = useState({ x: 0, y: 0 });

    useEffect(() => {
        fetch(FURNI_API_URL, { cache: "no-store" })
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (data?.results) setAllFurni(normaliseFurniWithImages(data.results)); })
            .catch(() => toast.error("Failed to load furni"))
            .finally(() => setLoading(false));
    }, []);

    const fetchSavedOffsets = useCallback(async () => {
        try {
            const json = await getBannerOffsets();
            if (json.success) setSavedOffsets(json.offsets || []);
        } catch {}
    }, []);

    useEffect(() => { fetchSavedOffsets(); }, [fetchSavedOffsets]);

    const slots = useMemo(() => getBannerSlots(allFurni), [allFurni]);

    // Deduplicated list — computed before guard so effects below can use it
    const seen = new Set();
    const eligibleItems = slots
        .map((item, slotIdx) => item ? { item, slotIdx } : null)
        .filter(entry => {
            if (!entry || seen.has(entry.item.id)) return false;
            seen.add(entry.item.id);
            return true;
        });

    const selectedEntry   = eligibleItems.find(e => e.item.id === selectedId) ?? eligibleItems[0] ?? null;
    const selectedSlotIdx = selectedEntry?.slotIdx ?? 0;
    const selectedItem    = selectedEntry?.item ?? null;

    // Auto-select first item once loaded — must be before guard
    useEffect(() => {
        if (!selectedId && eligibleItems.length > 0) setSelectedId(eligibleItems[0].item.id);
    }, [eligibleItems.length]); // eslint-disable-line

    // Restore saved offsets when selection changes — must be before guard
    useEffect(() => {
        if (!selectedItem) return;
        const existing = savedOffsets.find(o => o.furniName === selectedItem.name);
        if (existing) {
            const si  = existing.stateIndex    ?? 0;
            const ri  = existing.rotationIndex ?? 0;
            const idx = selectedItem.imageOpts.findIndex(o => o.stateIndex === si && o.rotationIndex === ri);
            setImageOptIdx(idx >= 0 ? idx : 0);
            setOffsetX(existing.offsetX ?? 0);
            setOffsetY(existing.offsetY ?? 0);
        } else {
            setImageOptIdx(0);
            setOffsetX(0);
            setOffsetY(0);
        }
    }, [selectedId]); // eslint-disable-line

    const currentOpt = selectedItem?.imageOpts?.[imageOptIdx];
    const totalOpts  = selectedItem?.imageOpts?.length ?? 0;

    // Build override map for the preview — only override the selected slot
    const overridesBySlot = useMemo(() => {
        if (!selectedItem || !currentOpt) return {};
        return {
            [selectedSlotIdx]: {
                url:     currentOpt.url,
                offsetX: offsetX,
                offsetY: offsetY,
            },
        };
    }, [selectedItem, selectedSlotIdx, currentOpt, offsetX, offsetY]);

    if (!userData || !userData.hasAdminRole) return <Navigate to="/" replace />;

    const handleImageNav = (delta) => {
        if (!selectedItem) return;
        setImageOptIdx(i => ((i + delta) % totalOpts + totalOpts) % totalOpts);
    };

    const handleSave = async () => {
        if (!selectedItem || !currentOpt) return;
        setSaving(true);
        try {
            const json = await upsertBannerOffset({
                furniUrl:      currentOpt.url,
                furniName:     selectedItem.name,
                offsetX,       offsetY,
                stateIndex:    currentOpt.stateIndex,
                rotationIndex: currentOpt.rotationIndex,
            });
            if (json.success) { toast.success("Offset saved!"); fetchSavedOffsets(); }
            else toast.error(json.message || "Failed to save");
        } catch { toast.error("Failed to save offset"); }
        finally  { setSaving(false); }
    };


    return (
        <AdminLayout>
            <ContentBox theme="default" tint="red" header="Banner Offsets">
                <p className="text-sm mb-4 text-gray-600">
                    The preview below is the real banner rendered at full scale and cropped to the selected slot. Offsets are pixel-perfect.
                </p>

                <div className="mb-4">
                    <label className="block text-xs uppercase mb-1 font-bold">Select Furni</label>
                    {loading ? (
                        <p className="text-sm text-gray-500 uppercase">Loading furni...</p>
                    ) : eligibleItems.length === 0 ? (
                        <p className="text-sm text-gray-500 uppercase">No furni loaded.</p>
                    ) : (
                        <select
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            value={selectedId || ""}
                            onChange={e => setSelectedId(e.target.value)}
                        >
                            {eligibleItems.map(({ item, slotIdx }) => (
                                <option key={item.id} value={item.id}>
                                    {item.name} ({item.size}), slot {slotIdx + 1}
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                {/* Preview + controls side by side */}
                {slots.some(Boolean) && (
                    <div className="flex flex-col lg:flex-row gap-6 items-start mb-6">

                        <div className="flex flex-col gap-1">
                            <BannerPreview
                                slots={slots}
                                selectedSlotIndex={selectedSlotIdx}
                                overridesBySlot={overridesBySlot}
                                onShiftReady={setAutoShift}
                            />
                            <p className="text-[11px] text-gray-400">
                                auto ({Math.round(autoShift.x)}, {Math.round(autoShift.y)}) | extra (+{offsetX}, +{offsetY})
                            </p>
                        </div>

                        {selectedItem && (
                            <div className="flex flex-col gap-4 flex-1">

                                {/* State / Rotation */}
                                {totalOpts > 1 && (
                                    <div>
                                        <label className="block text-xs uppercase mb-1 font-bold">State / Rotation</label>
                                        <div className="flex items-center gap-3">
                                            <button className="bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded text-sm font-bold" onClick={() => handleImageNav(-1)}>← Prev</button>
                                            <span className="text-sm flex-1 text-center font-medium">
                                                {currentOpt?.label} <span className="text-gray-400 text-xs">({imageOptIdx + 1}/{totalOpts})</span>
                                            </span>
                                            <button className="bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded text-sm font-bold" onClick={() => handleImageNav(1)}>Next →</button>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">Banner picks randomly by default. Saving locks it to this state/rotation.</p>
                                    </div>
                                )}

                                {/* Offset inputs */}
                                <div className="grid grid-cols-2 gap-3">
                                    {[["X", offsetX, setOffsetX], ["Y", offsetY, setOffsetY]].map(([axis, val, setVal]) => (
                                        <div key={axis}>
                                            <label className="block text-xs uppercase mb-1 font-bold">Extra {axis} Offset (px)</label>
                                            <div className="flex items-center gap-2">
                                                <button className="bg-gray-300 hover:bg-gray-400 px-3 py-2 rounded text-sm font-bold" onClick={() => setVal(v => v - 1)}>−</button>
                                                <input type="number" value={val} onChange={e => setVal(Number(e.target.value))} className="flex-1 px-2 py-2 border border-gray-300 rounded text-sm text-center" />
                                                <button className="bg-gray-300 hover:bg-gray-400 px-3 py-2 rounded text-sm font-bold" onClick={() => setVal(v => v + 1)}>+</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <p className="text-xs text-gray-500">Positive X shifts right. Negative Y shifts up.</p>

                                <div className="flex gap-2 mt-1">
                                    <button onClick={handleSave} disabled={saving} className="bg-maroon text-white px-5 py-2 text-sm uppercase tracking-wider disabled:opacity-50" style={{ boxShadow: "0 3px 0 0 rgba(0,0,0,0.3)" }}>
                                        {saving ? "Saving..." : "Save Override"}
                                    </button>
                                    <button
                                        onClick={async () => {
                                            if (!selectedItem) return;

                                            setSaving(true);
                                            try {
                                                const json = await deleteBannerOffset(selectedItem.name);
                                                if (json.success) {
                                                    setImageOptIdx(0);
                                                    setOffsetX(0);
                                                    setOffsetY(0);
                                                    toast.success("Override removed");
                                                    fetchSavedOffsets();
                                                } else {
                                                    toast.error(json.message || "Failed to remove override");
                                                }
                                            } catch {
                                                toast.error("Failed to remove override");
                                            } finally {
                                                setSaving(false);
                                            }
                                        }}
                                        disabled={saving}
                                        className="bg-gray-400 text-white px-5 py-2 text-sm uppercase tracking-wider disabled:opacity-50"
                                        style={{ boxShadow: "0 3px 0 0 rgba(0,0,0,0.3)" }}
                                    >
                                        {saving ? "Removing..." : "Reset"}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </ContentBox>
        </AdminLayout>
    );
}

export default BannerOffsets;